import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormControl, Validators} from '@angular/forms';
import {Apollo, gql} from 'apollo-angular';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {ToastrService} from 'ngx-toastr';
import {catchError, EMPTY, exhaustMap, filter, map, Subject, Subscription} from 'rxjs';
import {BuilderTemplateInput} from '../../graphql/globalTypes';
import {ErrorService} from '../../services/error.service';
import {TemplateService_GetTemplateItem_templateItem} from '../../services/__generated__/TemplateService_GetTemplateItem';
import {
  UpdateTemplateName_CreateUpdateBuilderTemplate,
  UpdateTemplateName_CreateUpdateBuilderTemplateVariables,
} from './__generated__/UpdateTemplateName_CreateUpdateBuilderTemplate';

const UPDATE_TITLE_MUTATION = gql`
  mutation UpdateTemplateName_CreateUpdateBuilderTemplate($input: BuilderTemplateInput) {
    updateOrCreateBuilderTemplate(input: $input) {
      templateItem {
      id
      title
      createPassword
    }
    error
  }
}
`;

@Component({
  selector: 'app-template-title',
  templateUrl: './template-title.component.html',
  styleUrls: ['./template-title.component.scss'],
  standalone: false,
})
export class TemplateTitleComponent implements OnInit, OnDestroy {
  templateItem: TemplateService_GetTemplateItem_templateItem | null = null;
  titleInput = new UntypedFormControl(null, {validators: Validators.required});
  submit$ = new Subject<string | null>();
  private subscriptions: Subscription[] = [];

  constructor(
    private bsModalRef: BsModalRef,
    private apollo: Apollo,
    private errService: ErrorService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.push(
      this.submit$
        .pipe(
          filter(() => this.titleInput.valid),
          map(() => [this.templateItem?.id, this.titleInput.value]),
          filter(([id]) => id),
          exhaustMap(([id, title]) => {
            const mutationInput: BuilderTemplateInput = {
              id: id,
              title: title,
            };

            return this.apollo.mutate<
              UpdateTemplateName_CreateUpdateBuilderTemplate,
              UpdateTemplateName_CreateUpdateBuilderTemplateVariables
            >({
              mutation: UPDATE_TITLE_MUTATION,
              variables: {
                input: mutationInput,
              },
            });
          }),
          catchError(error => {
            this.errService.handleDefault(error);
            return EMPTY;
          }),
        )
        .subscribe(response => {
          const error = response.data?.updateOrCreateBuilderTemplate.error;
          if (error) {
            this.toastr.error($localize`An error has occurred: ${error}.`);
          } else {
            this.toastr.success($localize`Title updated.`);
          }
          this.bsModalRef.hide();
        }),

      this.submit$.subscribe(() => {
        this.titleInput.updateValueAndValidity();
        if (this.titleInput.invalid) {
          this.toastr.error($localize`Please enter a valid template name`);
        }
      }),
    );

    if (this.templateItem) {
      this.titleInput.setValue(this.templateItem.title);
    }
  }

  closeModal(): void {
    this.bsModalRef.hide();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
